// The watchlist (SCENARIOS E5, PLAN-2026-10-03-research-tools.md §10):
// players a person keeps an eye on, held on the account, so a star in one
// league is a star in every league of that sport and on every device.
//
// `GET /api/me/watchlist` answers the account's list, every sport's, newest
// first (`?sport=` narrows it); `PUT` and `DELETE /api/me/watchlist/:playerId`
// star and unstar one player and answer the list again. A league's Players
// reads `availability=watching`, and its rows and the card say whether I
// watch each player (`PlayerRowView.watched`, `PlayerCardView.watched`).
//
// A STAR IS A NOTE TO SELF, never a signal to anybody else: nobody sees whom
// another person watches, and nothing counts stars.

import { z } from 'zod';
import { PlayerSummary } from './views';

/** How many players one sport's list holds: a list, not the pool. */
export const WATCHLIST_MAX = 200;

export const WatchedPlayer = z.object({
  sport: z.string(),
  /**
   * Who he is, as the directory has him today. No `onBye`: a bye is a week's,
   * and the account's list belongs to no league's week.
   */
  player: PlayerSummary.omit({ onBye: true }),
  addedAt: z.string(),
});
export type WatchedPlayer = z.infer<typeof WatchedPlayer>;

export const WatchlistView = z.object({
  /**
   * The sport this list is: the one asked for, or a starred player's after a
   * star. Null for every sport's.
   */
  sport: z.string().nullable(),
  /** Newest first. */
  players: z.array(WatchedPlayer),
  /** How many one sport's list holds (`WATCHLIST_MAX`). */
  max: z.number().int().min(1),
});
export type WatchlistView = z.infer<typeof WatchlistView>;

/** `?sport=`: one sport's list; absent, every sport's. */
export const WatchlistQuery = z.object({
  sport: z
    .string()
    .regex(/^[a-z]{2,20}$/, 'That is not a sport.')
    .optional(),
});
export type WatchlistQueryShape = z.infer<typeof WatchlistQuery>;
